/**
 * Compact opener for the Data Intelligence Review — the three-count strip from
 * the former right rail, placed top-right on Request Detail. Click opens the
 * review on this record so the reviewer can walk every open item.
 */
import clsx from 'clsx'

export type SmartReviewSummaryCounts = {
  open: number
  conflicts: number
  humanDecision: number
  autoExtracted: number
}

type Props = {
  counts: SmartReviewSummaryCounts
  onOpen: () => void
  disabled?: boolean
  className?: string
}

export default function SmartReviewSummaryCard({
  counts,
  onOpen,
  disabled = false,
  className,
}: Props) {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      title="Open the Data Intelligence Review — walk every uncertain or missing datapoint"
      className={clsx(
        'w-full max-w-[280px] shrink-0 rounded-lg border border-amber-400/15 bg-[#0a0a0a] px-2.5 py-2 text-left',
        'transition-colors hover:border-amber-400/35 hover:bg-black/60',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      data-testid="smart-review-summary-card"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 border-b border-white/[0.06] pb-1.5">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-stone-400">
          Data Intelligence Review
        </span>
        <span
          className={clsx(
            'text-[10px] font-mono',
            counts.open > 0 ? 'text-amber-300' : 'text-emerald-400/80',
          )}
        >
          {counts.open} open
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded border border-red-500/25 bg-red-500/[0.08] px-1.5 py-1.5">
          <div className="text-[15px] font-semibold leading-none text-red-300 tabular-nums">
            {counts.conflicts}
          </div>
          <div className="mt-1 text-[7px] font-mono uppercase leading-tight tracking-wide text-red-300/80">
            Conflicts
          </div>
        </div>
        <div className="rounded border border-amber-400/25 bg-amber-400/[0.08] px-1.5 py-1.5">
          <div className="text-[15px] font-semibold leading-none text-amber-200 tabular-nums">
            {counts.humanDecision}
          </div>
          <div className="mt-1 text-[7px] font-mono uppercase leading-tight tracking-wide text-amber-200/80">
            Human
            <br />
            Decision
          </div>
        </div>
        <div className="rounded border border-emerald-500/25 bg-emerald-500/[0.08] px-1.5 py-1.5">
          <div className="text-[15px] font-semibold leading-none text-emerald-300 tabular-nums">
            {counts.autoExtracted}
          </div>
          <div className="mt-1 text-[7px] font-mono uppercase leading-tight tracking-wide text-emerald-300/80">
            Auto-
            <br />
            Extracted
          </div>
        </div>
      </div>
    </button>
  )
}

export function smartReviewSummaryCounts(
  report: {
    conflicts?: unknown[] | null
    fields?: unknown[] | null
    review?: {
      open?: number
      counts?: { flagged?: number; missing?: number } | null
    } | null
    pendingRevisions?: unknown[] | null
    thread?: { loanUpdates?: { applied?: Record<string, unknown> | null } | null } | null
  } | null | undefined,
): SmartReviewSummaryCounts {
  // A revision to a deal already on the pipeline is not a package: the deal
  // holds everything else, so the only open items are the values it contests,
  // and what reached the deal on arrival is what was read. Scored as a
  // stand-alone package, a two-line reply reported 13 open items — 9 of them
  // "missing" fields the deal already holds — and not the contested rate.
  const waiting = report?.pendingRevisions?.length ?? 0
  if (waiting > 0 && report?.thread?.loanUpdates) {
    const reached = Object.keys(report.thread.loanUpdates.applied ?? {}).length
    return { open: waiting, conflicts: waiting, humanDecision: 0, autoExtracted: reached }
  }
  const open = report?.review?.open ?? 0
  const conflicts =
    report?.conflicts?.length ?? report?.review?.counts?.flagged ?? 0
  const humanDecision = report?.review?.counts?.missing ?? 0
  const autoExtracted = Math.max(0, (report?.fields?.length ?? 0) - open)
  return { open, conflicts, humanDecision, autoExtracted }
}
